import { showErrorAlert, showSuccessAlert } from 'actions';
import {
  deleteCustomerContract,
  deleteCustomerContractFailure,
  deleteCustomerContractRequest,
  deleteCustomerContractsuccess,
  downloadContract,
  fetchCustomerContracts,
  fetchCustomerContractsFailure,
  fetchCustomerContractsFulfill,
  fetchCustomerContractsRequest,
  fetchCustomerContractsSuccess,
  fetchSingleCustomerContract,
  fetchSingleCustomerContractFailure,
  fetchSingleCustomerContractRequest,
  fetchSingleCustomerContractsuccess,
  upsertCustomerContract,
  upsertCustomerContractFailure,
  upsertCustomerContractRequest,
  upsertCustomerContractsuccess,
} from 'actions/customerContracts';
import { apiClient as LitLingoClient } from 'client';
import { push } from 'connected-react-router';
import { resourceQueryParamName } from 'constants/resourceQueryNames';
import { uploadFileWithS3URL } from 'reducers/fileUploads';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getCustomerDomain } from 'selectors/auth';
import { getNavParamsByResource } from 'selectors/nav';
import { API, ErrorObject, SagaReturn } from 'types';
import { reverse } from 'utils/urls';
import { hashFileIncremental } from './fileUploads';

function* fetchSingleCustomerContractsaga(
  action: ReturnType<typeof fetchSingleCustomerContract>
): SagaReturn {
  const { payload } = action;
  yield put(fetchSingleCustomerContractRequest());
  const response = (yield call(
    [LitLingoClient.resources.customerContracts.extras, 'retrieve'],
    payload.uuid
  )) as API.Response<API.CustomerContracts.Retrieve>;
  if (response.error != null) {
    yield put(fetchSingleCustomerContractFailure(response.error));
  } else {
    yield put(fetchSingleCustomerContractsuccess(response.data));
  }
}

function* fetchCustomerContractsSaga(
  action: ReturnType<typeof fetchCustomerContracts>
): SagaReturn {
  const payloadParams = action?.payload || {};

  yield put(fetchCustomerContractsRequest());

  const resourceParams = (yield select(
    getNavParamsByResource(resourceQueryParamName.customer_contracts)
  )) as ReturnType<ReturnType<typeof getNavParamsByResource>>;

  const params: { include_count: boolean; kind?: string } = {
    include_count: true,
    ...resourceParams,
    ...payloadParams,
  };

  const response = (yield call([LitLingoClient.resources.customerContracts, 'list'], {
    params,
  })) as API.Response<API.CustomerContracts.List>;

  if (response.error != null) {
    yield put(fetchCustomerContractsFailure(response.error));
  } else {
    yield put(fetchCustomerContractsSuccess(response.data));
  }

  yield put(fetchCustomerContractsFulfill());
}

function* upsertCustomerContractsaga(
  action: ReturnType<typeof upsertCustomerContract>
): SagaReturn {
  const { payload } = action;
  const { file, ...rest } = payload.contract;
  const params = {};

  yield put(upsertCustomerContractRequest());

  try {
    let hashedFile: { fileHash: string; contentType: string } | null = null;
    let data = {};

    if (file) {
      hashedFile = (yield call(hashFileIncremental, file)) as {
        fileHash: string;
        contentType: string;
      };

      data = { ...rest, contract_file_hash: hashedFile?.fileHash };
    } else if (payload.action === 'delete') {
      data = { ...rest, contract_file_hash: '', contract_filename: '' };
    } else {
      data = rest;
    }

    const response = (yield call([LitLingoClient.resources.customerContracts, 'upsert'], {
      params,
      data,
    })) as API.Response<API.CustomerContracts.Upsert>;

    if (response.error != null) {
      yield put(upsertCustomerContractFailure(response.error));

      yield put(
        showErrorAlert("Something went wrong and the new statement can't be saved at this time")
      );
    } else {
      if (response.data.contract_upload?.upload_url && file) {
        yield put(
          uploadFileWithS3URL({
            file,
            fileName: rest.contract_filename || '',
            uploadURL: response.data.contract_upload.upload_url,
          })
        );
      }

      yield put(upsertCustomerContractsuccess(response.data));
      yield put(showSuccessAlert(`Statement has been created successfully`));
    }
  } catch (error) {
    const errorObject = error as ErrorObject;
    yield put(
      upsertCustomerContractFailure({
        message:
          typeof errorObject === 'object' && errorObject != null && 'message' in errorObject
            ? String((errorObject as { message?: string }).message)
            : 'Upload failed',
      })
    );
  }
}

function* deleteCustomerContractsaga(
  action: ReturnType<typeof deleteCustomerContract>
): SagaReturn {
  const { uuid, redirect } = action.payload;

  yield put(deleteCustomerContractRequest());

  const response = (yield call(
    [LitLingoClient.resources.customerContracts, 'delete'],
    uuid
  )) as API.Response<API.CustomerContracts.Upsert>;

  if (response.error != null) {
    yield put(deleteCustomerContractFailure(response.error));
  } else {
    yield put(deleteCustomerContractsuccess({ uuid }));
    yield put(showSuccessAlert('Statement deleted'));
    if (redirect) {
      const customerDomain =
        ((yield select(getCustomerDomain)) as ReturnType<typeof getCustomerDomain>) || '';

      const path = reverse({
        routeName: 'customer-contracts',
        customerDomain,
      });
      yield put(push(path));
    }
  }
}

function* downloadContractURLSaga(action: ReturnType<typeof downloadContract>): SagaReturn {
  const { contractId } = action.payload;

  const response = (yield call([LitLingoClient.resources.customerContracts.extras, 'downloadURL'], {
    urlParams: { contractId },
  })) as API.Response<API.CustomerContracts.DownloadURL>;

  if (response.error != null) {
    yield put(showErrorAlert('Something went wrong.'));
  } else {
    window.open(response.data.payload);
  }
}

function* customerContractsSaga(): SagaReturn {
  yield takeLatest(fetchSingleCustomerContract.toString(), fetchSingleCustomerContractsaga);
  yield takeLatest(fetchCustomerContracts.toString(), fetchCustomerContractsSaga);
  yield takeLatest(upsertCustomerContract.toString(), upsertCustomerContractsaga);
  yield takeLatest(deleteCustomerContract.toString(), deleteCustomerContractsaga);
  yield takeLatest(downloadContract.toString(), downloadContractURLSaga);
}

export default customerContractsSaga;
